package com.alpha.showcase.common.ui.play

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.toMutableStateList
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.alpha.showcase.common.ui.play.flip.FlipAxis
import com.alpha.showcase.common.ui.play.flip.FlippableContent
import kotlinx.coroutines.delay
import kotlin.random.Random.Default.nextBoolean
import kotlin.random.Random.Default.nextInt


@Composable
fun FrameWallLayout(
    row: Int,
    column: Int,
    pagingItems: PagingPlayItems,
    random: Boolean = false,
    duration: Long = DEFAULT_PERIOD,
    fitSize: Boolean = false
) {

    val gridSize = row * column
    // Re-key on generation so a background-sync refresh re-samples from the fresh
    // dataset instead of holding deleted/old items.
    val generation = pagingItems.generation
    // Load initial items from paging source
    val initialItems = remember(row, column, generation) {
        pagingItems.getRange(0, gridSize.coerceAtMost(pagingItems.size))
    }

    val reservedDataList = remember(row, column, generation) {
        // Items beyond the initial grid serve as the reserve pool
        val reserve = if (pagingItems.size > gridSize) {
            pagingItems.getRange(gridSize, (pagingItems.size - gridSize).coerceAtMost(PagingPlayItems.DEFAULT_PAGE_SIZE))
        } else {
            emptyList()
        }
        reserve.toMutableStateList()
    }

    // Track next index for sequential loading from paging source
    var nextPagedIndex by remember(generation) { mutableIntStateOf(gridSize + reservedDataList.size) }

    fun randomGet(): Any? {
        if (reservedDataList.isEmpty()) {
            // Refill from paging source
            if (nextPagedIndex < pagingItems.size) {
                val batch = pagingItems.getRange(nextPagedIndex, PagingPlayItems.DEFAULT_PAGE_SIZE.coerceAtMost(pagingItems.size - nextPagedIndex))
                nextPagedIndex += batch.size
                reservedDataList.addAll(batch)
            }
            if (reservedDataList.isEmpty()) {
                // Wrap around
                nextPagedIndex = 0
                val batch = pagingItems.getRange(0, gridSize.coerceAtMost(pagingItems.size))
                reservedDataList.addAll(batch)
            }
        }
        // markEmpty() (size -> 0) can land while an animation coroutine is still
        // mid-iteration: both refills above then produce nothing and nextInt(0)
        // would throw. Report exhaustion instead so the caller stops animating.
        if (reservedDataList.isEmpty()) return null
        val nextInt = nextInt(reservedDataList.size)
        return reservedDataList.removeAt(nextInt)
    }

    val currentShowFrameList = remember(row, column, generation) {
        val list = mutableListOf<Any>()
        list.addAll(initialItems)
        // Fill remaining slots if needed. When the paging source is (transiently)
        // empty, initialItems is empty too — fall back to blank placeholders
        // instead of an `index % 0` crash; the generation key re-runs this block
        // once real data lands.
        while (list.size < gridSize) {
            val filler = if (reservedDataList.isNotEmpty()) {
                randomGet()
            } else {
                initialItems.getOrNull(list.size % initialItems.size.coerceAtLeast(1))
            }
            list.add(filler ?: EMPTY_PLACEHOLDER)
        }
        list.toMutableStateList()
    }

    val mediaStates = rememberMediaItemStateStore(fitSize)
    Column {
        repeat(row) { i ->
            Row(modifier = Modifier.weight(1f / row)) {
                repeat(column) { j ->
                    Column(modifier = Modifier.weight(1f / column)) {
                        FlippableContent(
                            currentShowFrameList[i * column + j],
                            axis = if (kotlin.random.Random.nextBoolean()) FlipAxis.Vertical else FlipAxis.Horizontal
                        ){
                            PagerItem(
                                state = mediaStates.get(i * column + j, it, fitSize),
                                modifier = Modifier.padding(2.dp),
                                active = it == currentShowFrameList[i * column + j],
                                readMetadata = false,
                            )
                        }
                    }
                }
            }
        }
    }

    var replacementRound by remember(pagingItems, row, column, generation) { mutableIntStateOf(0) }
    rememberImagePlaybackProgress(duration, owner = listOf(pagingItems, row, column, generation), current = {
        val states = currentShowFrameList.mapIndexed { index, item -> mediaStates.get(index, item, fitSize) }
        ImagePlaybackFrame(replacementRound to states, states.all { it.ready },
            enabled = states.isNotEmpty() && pagingItems.size > 0)
    }) {
        val failed = currentShowFrameList.indices.filter {
            !mediaStates.get(it, currentShowFrameList[it], fitSize).ready
        }
        val firstColumn = if (column > 0) nextInt(column) else 0
        val indices = failed.ifEmpty {
            List(row) { (column * it + (firstColumn + it) % column) % currentShowFrameList.size }
        }
        // Keep the diagonal transition for a loaded grid; timed-out slots are replaced first.
        for (index in indices) {
            val replacement = randomGet() ?: break
            val previous = currentShowFrameList[index]
            currentShowFrameList[index] = replacement
            reservedDataList.add(previous)
            delay(800)
        }
        replacementRound++
    }
}

fun getRandomIntNoRe(bound: Int, candi: Int?): Int {
    require(bound > 0)
    if (bound == 1) return 0
    if (candi == null || candi !in 0 until bound) return nextInt(bound)
    val selected = nextInt(bound - 1)
    return if (selected >= candi) selected + 1 else selected
}

